import React from 'react';
import { CivilDate } from 'tc39-proposal-temporal';
import PropTypes from 'prop-types';
import { compareCivilDate, getCivilDateMonthStart, getCivilDateMonthEnd } from '@bingads-webui-universal/temporal-utitlities';
import { DateRange } from './date-range';
import { formatMonthDayYear } from '../common/date-util';

const getCivelDateFromValue = (value, i18n, dateFormat = 'M/d/yyyy') => {
  const parsedCivilDate = i18n.parseCivilDate(value, { raw: dateFormat });

  return parsedCivilDate ?
    new CivilDate(parsedCivilDate.year, parsedCivilDate.month, parsedCivilDate.day) :
    undefined;
};

export class DateRangeContainer extends React.Component {
  static defaultProps = {
    label: '',
    dateRange: null,
    onChangeSelectOption: null,
    onStartInputChange: null,
    onEndInputChange: null,
    selectedOption: undefined,
    highlightInputIndex: -1,
    isMonthRangePicker: false,
  };

  constructor(props) {
    super(props);
    const {
      i18n,
      timeZone,
      dateFormat,
      dateRange,
    } = props;
    this.state = ({
      start: formatMonthDayYear(dateRange.start, i18n, timeZone, dateFormat),
      end: formatMonthDayYear(dateRange.end, i18n, timeZone, dateFormat),
      prevPropsDateRange: props.dateRange,
    });
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const {
      i18n,
      timeZone,
      dateFormat,
      dateRange,
    } = nextProps;
    if (nextProps.dateRange !== prevState.prevPropsDateRange) {
      return {
        start: formatMonthDayYear(dateRange.start, i18n, timeZone, dateFormat),
        end: formatMonthDayYear(dateRange.end, i18n, timeZone, dateFormat),
        prevPropsDateRange: dateRange,
      };
    }
    return null;
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextState.start !== this.state.start ||
      nextState.end !== this.state.end ||
      nextProps.highlightInputIndex !== this.props.highlightInputIndex ||
      nextProps.selectedOption !== this.props.selectedOption ||
      !nextProps.dateRange.start ||
      !nextProps.dateRange.end ||
      !this.props.dateRange.start ||
      !this.props.dateRange.end ||
      compareCivilDate(nextProps.dateRange.start, this.props.dateRange.start) !== 0 ||
      compareCivilDate(nextProps.dateRange.end, this.props.dateRange.end) !== 0) {
      return true;
    }

    return false;
  }

  onStartInputChange = (value) => {
    this.setState({ start: value });
  }

  onEndInputChange = (value) => {
    this.setState({ end: value });
  }

  onStartChangeComplete = (event) => {
    const {
      i18n,
      timeZone,
      dateFormat,
      dateRange,
      isMonthRangePicker,
    } = this.props;
    const { value } = event.target;
    const civilDate = getCivelDateFromValue(value, i18n, dateFormat);

    if (civilDate) {
      this.props.onStartInputChange(isMonthRangePicker
        ? getCivilDateMonthStart(civilDate)
        : civilDate);
    } else {
      // invalid input, just roll back to last props
      this.setState({
        start: formatMonthDayYear(dateRange.start, i18n, timeZone, dateFormat),
      });
    }
  }

  onEndChangeComplete = (event) => {
    const {
      i18n,
      timeZone,
      dateFormat,
      dateRange,
      isMonthRangePicker,
    } = this.props;
    const { value } = event.target;
    const civilDate = getCivelDateFromValue(value, i18n, dateFormat);

    if (civilDate) {
      this.props.onEndInputChange(isMonthRangePicker
        ? getCivilDateMonthEnd(civilDate)
        : civilDate);
    } else {
      // invalid input, just roll back to last props
      this.setState({
        end: formatMonthDayYear(dateRange.end, i18n, timeZone, dateFormat),
      });
    }
  }

  render() {
    return (
      <DateRange
        label={this.props.label}
        start={this.state.start}
        end={this.state.end}
        highlightInputIndex={this.props.highlightInputIndex}
        onChangeSelectOption={e => this.props.onChangeSelectOption(Number(e.target.value))}
        onStartInputChange={e => this.onStartInputChange(e.target.value)}
        onEndInputChange={e => this.onEndInputChange(e.target.value)}
        onStartChangeComplete={this.onStartChangeComplete}
        onEndChangeComplete={this.onEndChangeComplete}
        selectedOption={this.props.selectedOption}
        i18n={this.props.i18n}
        onInputFocus={this.props.onInputFocus}
        startDateAriaLabel={this.props.startDateAriaLabel}
        endDateAriaLabel={this.props.endDateAriaLabel}
      />
    );
  }
}

DateRangeContainer.propTypes = {
  label: PropTypes.string,
  dateRange: PropTypes.shape({
    start: PropTypes.instanceOf(CivilDate),
    end: PropTypes.instanceOf(CivilDate),
  }),
  onChangeSelectOption: PropTypes.func,
  onStartInputChange: PropTypes.func,
  onEndInputChange: PropTypes.func,
  timeZone: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  selectedOption: PropTypes.shape({
    type: PropTypes.number,
    keyToExport: PropTypes.string,
    textToShow: PropTypes.string,
    textToExport: PropTypes.string,
    get: PropTypes.func,
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  highlightInputIndex: PropTypes.number,
  onInputFocus: PropTypes.func.isRequired,
  startDateAriaLabel: PropTypes.string.isRequired,
  endDateAriaLabel: PropTypes.string.isRequired,
  isMonthRangePicker: PropTypes.bool,
};
